import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRightLeft, Loader2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("pr_token")}` });

function AliasForm({ onSaved }) {
  const [erp, setErp] = useState("");
  const [sap, setSap] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/supplier-aliases`, { erp_code: erp, sap_codes: sap.split(/[,\s]+/), note }, { headers: headers() });
      toast.success(`${erp.toUpperCase()} now uses SAP list prices of ${sap.toUpperCase()}`);
      setErp(""); setSap(""); setNote(""); onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save mapping");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2 text-xs">
      <label className="flex flex-col gap-1">ERP supplier code<Input value={erp} onChange={(e) => setErp(e.target.value)} placeholder="RTG" className="h-8 w-32 font-mono" data-testid="alias-erp-input" /></label>
      <label className="flex flex-col gap-1">SAP supplier code(s)<Input value={sap} onChange={(e) => setSap(e.target.value)} placeholder="RAD-P4-S" className="h-8 w-48 font-mono" data-testid="alias-sap-input" /></label>
      <label className="flex flex-col gap-1 flex-1 min-w-[180px]">Note<Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Interplant — Goa P4" className="h-8" data-testid="alias-note-input" /></label>
      <Button size="sm" onClick={save} disabled={busy || !erp.trim() || !sap.trim()} className="bg-cyan-700 hover:bg-cyan-800 h-8" data-testid="alias-save-btn">
        {busy && <Loader2 size={14} className="animate-spin mr-1" />} Save mapping
      </Button>
    </div>
  );
}

export function SupplierAliasPanel() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/supplier-aliases`, { headers: headers() });
      setRows(data.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load mappings"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (erp) => {
    if (!window.confirm(`Remove mapping for ${erp}?`)) return;
    await axios.delete(`${API}/admin/supplier-aliases/${encodeURIComponent(erp)}`, { headers: headers() });
    toast.success(`Mapping for ${erp} removed`); load();
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="alias-panel">
      <div>
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><ArrowRightLeft size={15} className="text-cyan-700" /> Supplier code mapping (ERP → SAP)</h3>
        <p className="text-xs text-slate-500">When a PR is raised on the ERP supplier, list prices are looked up under the mapped SAP supplier code(s) — e.g. interplant RTG → RAD-P4-S.</p>
      </div>
      <AliasForm onSaved={load} />
      <table className="w-full text-xs" data-testid="alias-table">
        <thead className="text-[10px] uppercase text-slate-500 text-left"><tr><th className="py-1">ERP code</th><th>SAP code(s)</th><th>Note</th><th /></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="py-3 text-slate-400" data-testid="alias-empty">No mappings yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.erp_code} className="border-t border-slate-100" data-testid={`alias-row-${r.erp_code}`}>
              <td className="py-1.5 font-mono font-semibold">{r.erp_code}<div className="font-sans font-normal text-slate-500">{r.erp_name}</div></td>
              <td className="font-mono">{(r.sap_codes || []).map((c) => <div key={c}>{c} <span className="font-sans text-slate-500">{r.sap_names?.[c]}</span></div>)}</td>
              <td className="text-slate-600">{r.note}</td>
              <td className="text-right"><Button size="sm" variant="ghost" onClick={() => remove(r.erp_code)} data-testid={`alias-delete-${r.erp_code}`}><Trash2 size={14} className="text-rose-600" /></Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
